import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Cake, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PatientDialog from "@/components/PatientDialog";
import ClinicalFormView from "@/components/ClinicalFormView";

const ANAMNESIS_FIELDS = [
  { key: "chief_complaint", label: "Motivo de consulta", placeholder: "Ej: dolor en zona lumbar..." },
  { key: "current_symptoms", label: "Síntomas actuales", placeholder: "Describe los síntomas..." },
  { key: "medical_history", label: "Antecedentes médicos", placeholder: "Enfermedades previas, hospitalizaciones..." },
  { key: "surgical_history", label: "Antecedentes quirúrgicos", placeholder: "Cirugías previas..." },
  { key: "family_history", label: "Antecedentes familiares", placeholder: "Enfermedades en la familia..." },
  { key: "allergies", label: "Alergias", placeholder: "Medicamentos, alimentos, ambientales..." },
  { key: "medications", label: "Medicamentos actuales", placeholder: "Lista de medicamentos y dosis..." },
  { key: "habits", label: "Hábitos", placeholder: "Tabaco, alcohol, ejercicio, dieta..." },
  { key: "additional_notes", label: "Notas adicionales", placeholder: "Información relevante adicional..." },
];

const EMPTY_ANAMNESIS = ANAMNESIS_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [anamnesis, setAnamnesis] = useState(EMPTY_ANAMNESIS);
  const [saving, setSaving] = useState(false);
  const [editPatient, setEditPatient] = useState(false);

  const load = async () => {
    const [p, a] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/patients/${id}/anamnesis`),
    ]);
    setPatient(p.data);
    const data = a.data || {};
    setAnamnesis({ ...EMPTY_ANAMNESIS, ...ANAMNESIS_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: data[f.key] || "" }), {}) });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const handleSaveAnamnesis = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${id}/anamnesis`, anamnesis);
      toast.success("Anamnesis guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar esta clienta? Se borrará también su anamnesis.")) return;
    await api.delete(`/patients/${id}`);
    toast.success("Clienta eliminada");
    navigate("/app/pacientes");
  };

  if (!patient) {
    return <div className="p-12 text-[#6F5A4C]">Cargando...</div>;
  }

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <Link to="/app/pacientes" className="inline-flex items-center gap-2 text-sm text-[#6F5A4C] hover:text-[#3A2418] mb-6" data-testid="back-to-patients">
        <ArrowLeft className="w-4 h-4" /> Volver a clientas
      </Link>

      <header className="flex items-start gap-6 mb-10">
        <div className="w-16 h-16 rounded-full bg-[#E8DFD2] flex items-center justify-center font-display font-[700] text-2xl text-[#3A2418]">
          {patient.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <span className="label-eyebrow text-[#B5704A]">Clienta</span>
          <h1 className="font-display text-3xl font-[350] text-[#3A2418] mt-1" data-testid="patient-name">{patient.name}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#6F5A4C]">
            {patient.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{patient.email}</span>}
            {patient.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{patient.phone}</span>}
            {patient.birthdate && <span className="flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" />{patient.birthdate}</span>}
            {patient.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{patient.address}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditPatient(true)} className="rounded-full border-[#E8DFD2]" data-testid="edit-patient-btn">
            <Pencil className="w-4 h-4 mr-2" /> Editar
          </Button>
          <Button variant="outline" onClick={handleDelete} className="rounded-full text-[#B5704A] border-[#E8DFD2] hover:bg-[#B5704A]/10 hover:text-[#B5704A]" data-testid="delete-patient-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <Tabs defaultValue="ficha" className="w-full">
        <TabsList className="bg-white border border-[#E8DFD2]" data-testid="patient-tabs">
          <TabsTrigger value="ficha" className="data-[state=active]:bg-[#B5704A] data-[state=active]:text-white" data-testid="tab-ficha">Ficha clínica</TabsTrigger>
          <TabsTrigger value="anamnesis" className="data-[state=active]:bg-[#B5704A] data-[state=active]:text-white" data-testid="tab-anamnesis">Notas internas</TabsTrigger>
          <TabsTrigger value="info" className="data-[state=active]:bg-[#B5704A] data-[state=active]:text-white" data-testid="tab-info">Información</TabsTrigger>
        </TabsList>

        <TabsContent value="ficha" className="mt-6">
          <ClinicalFormView patient={patient} />
        </TabsContent>

        <TabsContent value="anamnesis" className="mt-6">
          <div className="surface-card p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-xl font-[600] text-[#3A2418]">Notas internas / Anamnesis</h2>
                <p className="text-sm text-[#6F5A4C] mt-1">Tus apuntes profesionales sobre esta clienta</p>
              </div>
              <Button onClick={handleSaveAnamnesis} disabled={saving} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="save-anamnesis-btn">
                {saving ? "Guardando..." : "Guardar anamnesis"}
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {ANAMNESIS_FIELDS.map((f) => (
                <div key={f.key} className={f.key === "additional_notes" ? "md:col-span-2" : ""}>
                  <Label className="text-sm font-medium text-[#3A2418]">{f.label}</Label>
                  <Textarea
                    value={anamnesis[f.key] || ""}
                    onChange={(e) => setAnamnesis({ ...anamnesis, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={3}
                    className="mt-1.5 bg-white border-[#E8DFD2] focus-visible:ring-[#B5704A]"
                    data-testid={`anamnesis-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="info" className="mt-6">
          <div className="surface-card p-8 space-y-4">
            <div>
              <div className="label-eyebrow text-[#6F5A4C] text-[10px]">Nombre</div>
              <div className="text-[#3A2418] mt-1">{patient.name}</div>
            </div>
            <div>
              <div className="label-eyebrow text-[#6F5A4C] text-[10px]">Email</div>
              <div className="text-[#3A2418] mt-1">{patient.email || "—"}</div>
            </div>
            <div>
              <div className="label-eyebrow text-[#6F5A4C] text-[10px]">Teléfono</div>
              <div className="text-[#3A2418] mt-1">{patient.phone || "—"}</div>
            </div>
            <div>
              <div className="label-eyebrow text-[#6F5A4C] text-[10px]">Notas internas</div>
              <div className="text-[#3A2418] mt-1 whitespace-pre-wrap">{patient.notes || "—"}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <PatientDialog
        open={editPatient}
        onClose={() => setEditPatient(false)}
        onSaved={load}
        editing={patient}
      />
    </div>
  );
}
